import { GraphQLClient } from 'graphql-request';

import { GeneralLedgerProcessingStatus } from '../../../gql/graphql';

export const drawerWidth = 220;
export const apiUrl = import.meta.env.VITE_API_URL; // todo - find out the correct way to do this

export const graphQLClient = new GraphQLClient(`${apiUrl}/graphql`, {
  credentials: 'include',
  mode: 'cors',
  // Setup a request middleware that pulls the operation name from the request and sets it as a query param: ?o=operationName
  requestMiddleware: (options) => {
    // Serialize variables in base64
    const isQuery = JSON.parse(options?.body?.toString() || '{}')[
      'query'
    ].startsWith('query');
    options.url = `${options.url}?${isQuery ? 'q' : 'm'}=${options.operationName}`;
    return options;
  },
});

export const allRoles = ['user', 'administrator', 'owner'];

export const CacheKeys = {
  acceptTerms: 'acceptTerms',
  activeFiscalYears: 'activeFiscalYears',
  activeFiscalYearsForTools: 'activeFiscalYearsForTools',
  adminDistrictFeatures: 'adminDistrictFeatures',
  afrReports: 'afrReports',
  budgetReports: 'budgetReports',
  budgetRevisionReports: 'budgetRevisionReports',
  budgetFileDetails: 'budgetFileDetails',
  budgetRevisionDetails: 'budgetRevisionDetails',
  budgetRevisionPrereqFiles: 'budgetRevisionPrereqFiles',
  codeCheck: 'codeCheck',
  compStudy: 'compStudy',
  csrChartData: 'csrChartData',
  chartOfAccountsFile: 'chartOfAccountsFile',
  ctedReports: 'ctedReports',
  currentUser: 'currentUser',
  currentUserScopesForAccount: 'currentUserScopesForAccount',
  districtCoaSegments: 'districtCoaSegments',
  entities: 'entities',
  entityDetails: 'entityDetails',
  entityFiles: 'entityFiles',
  entityUsers: 'entityUsers',
  idcReports: 'idcReports',
  invitations: 'invitations',
  jobResult: 'jobResult',
  ledgers: 'ledgers',
  notifications: 'notifications',
  previousCodeChecks: 'previousCodeChecks',
  schools: 'schools',
  templateVersion: 'templateVersion',
};

export const Links = {
  blog: 'https://ngsb.io/blog',
  teacherCompStudy:
    'https://app.powerbi.com/view?r=eyJrIjoiNTBmN2RmMGEtM2M2Ni00ZGMyLWI3MzQtNjliOTkzN2RlNjRiIiwidCI6IjE5MmY4MTYzLTAyMGEtNDU2Yy1hYjZjLTA4ZDk0ZDI0Y2FlMSIsImMiOjZ9',
  totalCompStudy:
    'https://app.powerbi.com/view?r=eyJrIjoiOTljOGExMzAtYjE2YS00OTk4LTg5NzktMGU5MWQxMjg1ZjBiIiwidCI6IjE5MmY4MTYzLTAyMGEtNDU2Yy1hYjZjLTA4ZDk0ZDI0Y2FlMSIsImMiOjZ9',
  privacy:
    'https://app.termly.io/embed/terms-of-use/52752839-48b0-4306-8eb0-2ca5a730394b',
  resources: 'https://ngsb.io/resources',
  terms:
    'https://app.termly.io/embed/terms-of-use/9a76919f-7ba8-47eb-9ae0-6ed565f61260',
};

export const Scopes = {
  accountCodeGenerator: 'nextgen:account_code_generator.read',
  aiAssistant: 'nextgen:ai_assistant.read',
  budgetTool: 'nextgen:budget_tool.edit',
  codeCheck: 'nextgen:general_ledgers.edit',
  compStudy: 'nextgen:salary_comparison.read',
  cted: 'nextgen:cted_reports.edit',
  files: 'nextgen:files.edit',
  indirectCosts: 'nextgen:indirect_cost_reports.edit',
};

export const ExcelCurrencyFormat = '$#,##0.00_);[Red]($#,##0.00)';

// Keep in sync with the Classification enum in @app/worker/src/types/enums
export enum CodeCheckClassification {
  '6150_IN_1000' = '6150-in-1000',
  '6100_IN_1900' = '6100-in-1900',
  INVALID_COMBOS = 'invalid-combos',
  INVALID_FUND = 'invalid-fund',
  NEGATIVE_EXPENDITURE = 'negative-expenditure',
  OTHER = 'other',
  TRANSFER = 'transfer',
  UNUSUAL_COMBOS = 'unusual-combos',
  USE_DETAILED_CODES = 'use-detailed-codes',
}

export const DisplayClassifications: Record<CodeCheckClassification, string> = {
  [CodeCheckClassification['6150_IN_1000']]: '6150 in 1000',
  [CodeCheckClassification['6100_IN_1900']]: '6100 in 1900',
  [CodeCheckClassification.INVALID_COMBOS]: 'Invalid Combos',
  [CodeCheckClassification.INVALID_FUND]: 'Invalid Fund',
  [CodeCheckClassification.NEGATIVE_EXPENDITURE]: 'Negative Expenditure',
  [CodeCheckClassification.OTHER]: 'Other',
  [CodeCheckClassification.TRANSFER]: 'Transfer',
  [CodeCheckClassification.UNUSUAL_COMBOS]: 'Unusual Combos',
  [CodeCheckClassification.USE_DETAILED_CODES]: 'Use Detailed Codes',
};

// Keep in sync with the ProgressStep enum in @app/worker/src/types/enums.ts
export enum ProcessingStep {
  CODE_CHECK = 'CODE_CHECK',
  NORMALIZATION = 'NORMALIZATION',
}

export const DisplayProgressStep: Record<ProcessingStep, string> = {
  [ProcessingStep.CODE_CHECK]: 'Account Code Check',
  [ProcessingStep.NORMALIZATION]: 'Ledger Normalization',
};

export const ProcessingStatusColors: Record<
  GeneralLedgerProcessingStatus,
  string | null
> = {
  [GeneralLedgerProcessingStatus.Complete]: '#9BF471',
  [GeneralLedgerProcessingStatus.Error]: '#FFB2B2',
  [GeneralLedgerProcessingStatus.Pending]: '#FFD166',
  [GeneralLedgerProcessingStatus.StoppedEarly]: '#FFE586',
};

export const TOOLS = {
  AFR: 'AFR',
  BUDGET_PROPOSAL_WIZARD: 'BUDGET_PROPOSAL_WIZARD',
  BUDGET_REVISION: 'BUDGET_REVISION',
  BUDGET_REVISION_WIZARD: 'BUDGET_REVISION_WIZARD',
  CODE_CHECK: 'CODE_CHECK',
  CTED: 'CTED',
  IDC: 'IDC',
  SETTINGS: 'SETTINGS',
};

export type Tool = keyof typeof TOOLS;

export type FiscalYear = {
  id: string;
  preview: boolean;
  budgetRevisionMode: boolean;
  value: number;
};

export const DEFAULT_FISCAL_YEAR: FiscalYear = {
  id: '',
  preview: false,
  budgetRevisionMode: false,
  value: new Date().getFullYear(),
};

export const DEFAULT_ENTITY_PREVIOUS_FISCAL_YEAR = {
  value: DEFAULT_FISCAL_YEAR.value - 1,
};

// From FY2025 the Food Service AFR is part of the main AFR, so only AFRs up to
// FY2025 need the prior year's separate Food Service AFR. Matches the worker's
// FOOD_SERVICE_MERGE_YEAR.
export const FOOD_SERVICE_MERGE_YEAR = 2025;

export enum DistrictTitle {
  ASSESSMENTS_COORDINATOR = 'Assessments Coordinator',
  AZEDS_COORDINATOR = 'AzEDS/ADM Data Coordinator',
  BOOKSTORE_MANAGER = 'Bookstore Manager',
  BUSINESS_MANAGER_1 = 'Business Manager 1',
  BUSINESS_MANAGER_2 = 'Business Manager 2',
  BUSINESS_CONSULTANT = 'Business Consultant',
  CFO = 'Chief Financial Officer',
  CTE_COORDINATOR = 'CTE Coordinator',
  CURRICULUM_COORDINATOR = 'Curriculum Coordinator',
  EA_TO_SUPERINTENDENT = 'Executive Assistant to Superintendent',
  GOVERNING_BOARD_MEMBER = 'Governing Board Member',
  IT_DIRECTOR = 'Information Technology (IT) Director',
  POVERTY_COORDINATOR = 'Poverty Coordinator',
  SDER_COORDINATOR = 'School District Employee Report (SDER) Coordinator',
  SPED_COORDINATOR = 'SPED Data Reporting Coordinator',
  SUPERINTENDENT = 'Superintendent',
  TRANSPORTATION_COORDINATOR = 'Transportation Data Reporting Coordinator',
}

export const DEFAULT_DISTRICT_TITLES: DistrictTitle[] = [
  DistrictTitle.SUPERINTENDENT,
  DistrictTitle.EA_TO_SUPERINTENDENT,
  DistrictTitle.CFO,
  DistrictTitle.BUSINESS_MANAGER_1,
  DistrictTitle.BUSINESS_MANAGER_2,
  DistrictTitle.BUSINESS_CONSULTANT,
  DistrictTitle.SDER_COORDINATOR,
  DistrictTitle.SPED_COORDINATOR,
  DistrictTitle.AZEDS_COORDINATOR,
  DistrictTitle.TRANSPORTATION_COORDINATOR,
  DistrictTitle.CTE_COORDINATOR,
  DistrictTitle.POVERTY_COORDINATOR,
  DistrictTitle.ASSESSMENTS_COORDINATOR,
  DistrictTitle.CURRICULUM_COORDINATOR,
  DistrictTitle.IT_DIRECTOR,
  DistrictTitle.BOOKSTORE_MANAGER,
  DistrictTitle.GOVERNING_BOARD_MEMBER,
  DistrictTitle.GOVERNING_BOARD_MEMBER,
  DistrictTitle.GOVERNING_BOARD_MEMBER,
  DistrictTitle.GOVERNING_BOARD_MEMBER,
  DistrictTitle.GOVERNING_BOARD_MEMBER,
  DistrictTitle.GOVERNING_BOARD_MEMBER,
  DistrictTitle.GOVERNING_BOARD_MEMBER,
  DistrictTitle.GOVERNING_BOARD_MEMBER,
  DistrictTitle.GOVERNING_BOARD_MEMBER,
];

export const DISTRICT_TITLES_BY_YEAR: Record<number, DistrictTitle[]> = {
  2022: DEFAULT_DISTRICT_TITLES,
  2023: DEFAULT_DISTRICT_TITLES,
  2024: DEFAULT_DISTRICT_TITLES,
  2025: DEFAULT_DISTRICT_TITLES,
  2026: [
    DistrictTitle.SUPERINTENDENT,
    DistrictTitle.BUSINESS_MANAGER_1,
    DistrictTitle.GOVERNING_BOARD_MEMBER,
    DistrictTitle.GOVERNING_BOARD_MEMBER,
    DistrictTitle.GOVERNING_BOARD_MEMBER,
    DistrictTitle.GOVERNING_BOARD_MEMBER,
    DistrictTitle.GOVERNING_BOARD_MEMBER,
    DistrictTitle.GOVERNING_BOARD_MEMBER,
    DistrictTitle.GOVERNING_BOARD_MEMBER,
    DistrictTitle.GOVERNING_BOARD_MEMBER,
    DistrictTitle.GOVERNING_BOARD_MEMBER,
  ],
};

export const ALLOWED_ERRORS: Record<string, string[]> = {
  QUICK_CHECK: ['Account code format is incorrect'],
};
